import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveGame, selectActivePokemon, selectHand, selectOptions } from '../ActiveGame/selectors';
import { setContextMenu } from '../ActiveGame/actions';
import { HAND } from '../ActiveGame/contextMenuTypes';
import ContextMenu from '../ContextMenu';
import CardDetail from '../ContextMenu/CardDetail';
import HandMenu from './HandMenu';
import DraggableHandCard from './DraggableHandCard';
import { handCardAction } from './handCardAction';
import { Label, NewMark, RefusalNote, Tray } from './styled';
import useNewInHand from './useNewInHand';
import { scrollToShow } from './scrollToShow';
import { selectPicksSource } from '../Picks/selectors';
import { TRAINER as TRAINER_PICKS } from '../Picks/constants';
import { arrangeHand } from '../Arrange/actions';
import Anchor from '../Motion/Anchor';
import { anchors } from '../Motion/anchorNames';
import DropTarget from '../Drag/DropTarget';
import { useDragging } from '../Drag/dropTargets';
import { DECK_TO_HAND } from '../Deck/constants';
import { draw } from '../Deck/actions';
import { usePendingAttachFromHand } from '../Undo/usePendingAttach';

/**
 * Your hand, always on screen. Tapping a card opens what you can do with it; dragging it up plays it on the board.
 * Dragging your deck onto it draws your turn's card. Cards that just came in are marked new for a moment, and the
 * hand scrolls to them (useNewInHand).
 */
const Hand = ({ userId }) => {
  const dispatch = useDispatch();
  const hand = useSelector((state) => selectHand(state, { userId }));
  const [chosenPivotId, choose] = useState(null);
  // Why the card you just tried to drag can't be played (DraggableHandCard), said where the hand's label is.
  const [refusal, setRefusal] = useState(null);
  const game = useSelector(selectActiveGame);
  const activePokemon = useSelector((state) => selectActivePokemon(state, { userId }));
  const options = useSelector(selectOptions);
  const dragging = useDragging();
  const trayRef = useRef(null);
  const picksSource = useSelector(selectPicksSource);
  // Energy dragged onto a Pokémon shows there while its Undo waits (usePendingAttach): gone from here meanwhile.
  const attachingFromHand = usePendingAttachFromHand();
  const playingTrainer = picksSource && picksSource.get('kind') === TRAINER_PICKS ? picksSource.get('handPosition') : null;
  const pivotIds = hand ? hand.get('cards').sortBy(card => card.get('order')).map(card => card.get('pivot_id')).toArray() : [];
  const fresh = useNewInHand(pivotIds);

  // Bring what just came in into view: the hand scrolls sideways on a phone.
  useEffect(() => {
    const tray = trayRef.current;
    if (!tray || !fresh.size) {
      return;
    }
    const trayBox = tray.getBoundingClientRect();
    const items = [...fresh].map(id => tray.querySelector(`[data-pivot="${id}"]`)).filter(Boolean).map((element) => {
      const box = element.getBoundingClientRect();
      return { left: box.left - trayBox.left + tray.scrollLeft, right: box.right - trayBox.left + tray.scrollLeft };
    });
    tray.scrollTo({ left: scrollToShow({ scrollLeft: tray.scrollLeft, width: tray.clientWidth }, items), behavior: 'smooth' });
  }, [fresh]);
  const drawingHere = !!dragging && dragging.kind === DECK_TO_HAND;
  const situation = useMemo(
    () => ({ settingUp: !game.get('active_player_id'), hasActive: !!activePokemon, options }),
    [game, activePokemon, options],
  );

  if (!hand) {
    return null;
  }

  const cards = hand.get('cards').sortBy(card => card.get('order'));
  const chosen = cards.find(card => card.get('pivot_id') === chosenPivotId);

  return (
    <>
      {refusal ? <RefusalNote role="status">{refusal}</RefusalNote> : <Label>Your hand · {cards.size}</Label>}
      {drawingHere && <DropTarget name={anchors.hand(userId)} commit={() => dispatch(draw())} />}
      <Anchor name={anchors.hand(userId)}>
        <Tray aria-label="Your hand" $lit={drawingHere} ref={trayRef}>
          {cards.map(card => (
            <Anchor key={card.get('pivot_id')} name={anchors.handCard(userId, card.get('pivot_id'))}>
              {/* Another hand card dropped on this one moves to its place (and doesn't play). */}
              {dragging && dragging.handPosition !== undefined && dragging.handPosition !== card.get('order') && (
                <DropTarget
                  name={anchors.handCard(userId, card.get('pivot_id'))}
                  commit={() => {
                    dragging.backOut();
                    dispatch(arrangeHand(dragging.handPosition, cards.indexOf(card)));
                  }}
                />
              )}
              <NewMark
                data-pivot={card.get('pivot_id')}
                $new={fresh.has(card.get('pivot_id'))}
                // A Trainer being played lies on the board (TrainerInPlay); here it's set aside.
                style={playingTrainer === card.get('order') ? { opacity: 0.3 }
                  : attachingFromHand === card.get('order') ? { opacity: 0 } : undefined}
              >
                <DraggableHandCard
                card={card}
                place={anchors.handCard(userId, card.get('pivot_id'))}
                action={handCardAction(card, situation)}
                onRefused={setRefusal}
                onTap={() => {
                  choose(card.get('pivot_id'));
                  dispatch(setContextMenu(HAND, userId));
                }}
                />
              </NewMark>
            </Anchor>
        ))}
      </Tray>
      </Anchor>
      {chosen && <ContextMenu menu={HAND} userId={userId} title={chosen.get('name')}>
        <CardDetail card={chosen}>
          <HandMenu card={chosen} userId={userId} />
        </CardDetail>
      </ContextMenu>}
    </>
  );
};

Hand.propTypes = {
  userId: PropTypes.number.isRequired,
};

export default Hand;
